import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { BehaviorSubject, Subscription, forkJoin, map, of } from 'rxjs';
import { ConfigService } from './config.service';
import {
  CatalogCellValue,
  CatalogEntityConfig,
  CatalogNavItem,
  CatalogRow,
  CatalogTypeOption,
  DomainCatalogConfig,
  DomainCatalogPayload,
  DomainCatalogViewModel,
  getCatalogSaveFailureMessage,
  getPendingCatalogCardCounts,
  getPendingCatalogChangeCount,
  getSaveResponseError,
  parseDuplicateSaveFailure,
} from '../models/domain-catalog.models';

/** The unsaved staging sets for a single catalog, snapshotted when the user switches cards. */
interface CatalogPendingSnapshot {
  editingIds: Set<string>;
  editValues: Map<string, CatalogRow>;
  pendingInsertIds: Set<string>;
  pendingToggleIds: Set<string>;
  pendingDeleteIds: Set<string>;
}

const API_FIELD_ALIASES: Record<string, string[]> = {
  businessSegment: ['businessSegment', 'business_segment', 'businessUnit', 'business_unit', 'name', 'code'],
  description: ['description', 'desc'],
  department: ['department', 'dept'],
  owner: ['owner', 'owner_name', 'ownerName'],
  budget: ['budget'],
  status: ['status'],
  created: ['created', 'createdDate', 'created_date', 'createdOn', 'created_on', 'createdAt', 'created_at'],
  createdBy: ['createdBy', 'created_by', 'createdUser', 'created_user'],
  updatedDate: ['updatedDate', 'updated_date', 'updatedOn', 'updated_on', 'updatedAt', 'updated_at', 'modifiedDate', 'modified_date'],
  updatedBy: ['updatedBy', 'updated_by', 'modifiedBy', 'modified_by'],
};

const CATALOG_DROPDOWN_PAYLOAD: Record<string, unknown> = {
  uuid: [null],
  is_deleted: false,
  active_flag: "Yes",
};

const CATALOG_TYPES: CatalogTypeOption[] = [
  { id: 'products', label: 'Products', description: 'Product catalog, SKUs, and item master data.' },
  { id: 'price-levels', label: 'Price Levels', description: 'Pricing tiers and rate structures.' },
  { id: 'customer-segments', label: 'Customer Segments', description: 'Customer groupings and segment hierarchies.' },
  { id: 'currencies', label: 'Currencies', description: 'Currency codes and exchange rate references.' },
  { id: 'tax-codes', label: 'Tax Codes', description: 'Tax classification and jurisdiction codes.' },
  { id: 'ship-to-locations', label: 'Ship-to Locations', description: 'Delivery addresses and logistics nodes.' },
];

const EMPTY_VM: DomainCatalogViewModel = {
  loading: true,
  sidebarCollapsed: false,
  activeCatalogId: '',
  config: null,
  navItems: [],
  rows: [],
  filteredRows: [],
  filters: {},
  selectedRowId: null,
  editingIds: new Set<string>(),
  editValues: new Map<string, CatalogRow>(),
  pendingInsertIds: new Set<string>(),
  pendingToggleIds: new Set<string>(),
  pendingDeleteIds: new Set<string>(),
  pendingCounts: {},
  isAdding: false,
  draftRow: null,
  showAddCatalogModal: false,
  showDeleteConfirmModal: false,
  deleteConfirmRowId: null,
  newCatalogType: '',
  availableCatalogTypes: CATALOG_TYPES,
  toastMessage: '',
  toastVisible: false,
  toastVariant: 'success',
  isCompact: false,
};

/** Default endpoint that supplies the Business Unit dropdown options for Add Row forms. */
const BUSINESS_UNIT_OPTIONS_URL = 'http://localhost:4200/domain-catalogs';
/** Default endpoint that supplies the Region dropdown options for Add Row forms. */
const REGION_OPTIONS_URL = '/spriced/platform/region/dynamic_filter';
/** Default endpoint that supplies the Country dropdown options for Add Row forms. */
const COUNTRY_OPTIONS_URL = '/spriced/platform/country/dynamic_filter';

@Injectable({
  providedIn: 'root',
})
export class DomainCatalogDataService implements OnDestroy {
  private readonly vmSubject = new BehaviorSubject<DomainCatalogViewModel>(EMPTY_VM);
  private readonly subs = new Subscription();
  private catalogRows: Record<string, CatalogRow[]> = {};
  /**
   * Unsaved staging state for every catalog the user is NOT currently viewing.
   * The active catalog's staging lives on the view model; when the user switches
   * cards it is snapshotted here so returning restores the pending rows exactly.
   */
  private pendingByCatalog: Record<string, CatalogPendingSnapshot> = {};
  private businessUnitApiOptions: string[] = [];
  private regionApiOptions: string[] = [];
  private countryApiOptions: string[] = [];
  private apiLoadedCatalogIds = new Set<string>();
  private toastTimer?: ReturnType<typeof setTimeout>;
  private loadedConfigUrl = '';
  private loadedDataUrl = '';
  private isLoading = false;
  private dataUrlOverride?: string;

  readonly vm$ = this.vmSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly globalFilterService: GlobalFilterService,
    private readonly configService: ConfigService,
  ) {
    this.subs.add(
      this.globalFilterService.filter$.subscribe((filters) => {
        if (filters?.widgetId === 'domain-catalogs') return;
        this.applyIncomingFilters(filters?.requestParams || {});
      }),
    );
  }

  load(configUrl = 'assets/dashboard-config/domain-catalogs.json', dataUrlOverride?: string): void {
    const requestedDataUrl = dataUrlOverride || '';
    if (
      this.isLoading &&
      this.loadedConfigUrl === configUrl &&
      this.loadedDataUrl === requestedDataUrl
    ) {
      return;
    }

    this.loadedConfigUrl = configUrl;
    this.loadedDataUrl = requestedDataUrl;
    this.dataUrlOverride = dataUrlOverride;
    this.isLoading = true;
    this.patch({ loading: true });
    this.subs.add(
      this.http.get<DomainCatalogConfig>(configUrl).pipe(
        map((config) => ({ config, dataUrl: dataUrlOverride || config.apiUrl })),
      ).subscribe({
        next: ({ config, dataUrl }) => this.loadRows(config, dataUrl),
        error: () => {
          this.isLoading = false;
          this.patch({ loading: false });
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  get activeConfig(): CatalogEntityConfig | null {
    const vm = this.vmSubject.value;
    return vm.config?.entities.find((entity) => entity.id === vm.activeCatalogId) || null;
  }

  get currentConfig(): DomainCatalogConfig | null {
    return this.vmSubject.value.config;
  }

  get businessUnitOptions(): string[] {
    return this.businessUnitApiOptions;
  }

  get regionOptions(): string[] {
    return this.regionApiOptions;
  }

  get countryOptions(): string[] {
    return this.countryApiOptions;
  }

  selectCatalog(catalogId: string): void {
    const vm = this.vmSubject.value;
    // A tabbed entity (e.g. DOA) is a container: selecting it opens its first tab.
    const requested = vm.config?.entities.find((e) => e.id === catalogId);
    if (requested?.tabs?.length) {
      catalogId = requested.tabs[0].id;
    }
    if (vm.activeCatalogId === catalogId) return;

    // Preserve the pending work on the card being left, then restore whatever
    // was staged on the card being opened. Switching cards never discards edits.
    this.snapshotPending(vm);
    const restored = this.restorePending(catalogId);

    this.patch({
      activeCatalogId: catalogId,
      filters: {},
      selectedRowId: null,
      ...restored,
      isAdding: false,
      draftRow: null,
    });
    this.updatePendingCounts();
    this.globalFilterService.clearAllFilters();

    // The DOA forms show Business Unit / Region dropdowns in place of the
    // refine panel, so their options must be ready as soon as the tab opens.
    if (catalogId === 'doa-range-level' || catalogId === 'doa-approver-level') {
      this.loadBusinessUnitOptions(CATALOG_DROPDOWN_PAYLOAD);
    }
    if (catalogId === 'doa-approver-level') {
      this.loadRegionOptions(CATALOG_DROPDOWN_PAYLOAD);
    }

    const entity = vm.config?.entities.find((e) => e.id === catalogId);
    if (entity?.dataApi) {
      if (!this.apiLoadedCatalogIds.has(catalogId)) {
        this.loadEntityRowsFromApi(entity);
      } else {
        this.refreshRows();
      }
      return;
    }

    const dataUrl = this.dataUrlOverride || vm.config?.apiUrl || '';
    if (dataUrl.startsWith('http') && !this.catalogRows[catalogId]) {
      this.patch({ loading: true });
      this.fetchEntityRows(vm.config!, dataUrl, catalogId);
    } else {
      this.refreshRows();
    }
  }

  toggleSidebar(): void {
    this.patch({ sidebarCollapsed: !this.vmSubject.value.sidebarCollapsed });
  }

  setCompact(isCompact: boolean): void {
    if (this.vmSubject.value.isCompact !== isCompact) {
      this.patch({ isCompact, sidebarCollapsed: isCompact });
    }
  }

  setFilter(key: string, value: string): void {
    const nextFilters = { ...this.vmSubject.value.filters, [key]: value };
    if (!value) delete nextFilters[key];

    this.patch({ filters: nextFilters });
    // Internal keys (the DOA free-text search) only filter locally; they are
    // not part of any entity's filter config, so other widgets never see them.
    if (key.startsWith('__')) {
      this.refreshRows();
      return;
    }
    const listener = this.activeConfig?.filterListener || [];
    if (value) {
      this.globalFilterService.updateFilterSelection(key, [value], false, 'domain-catalogs', true, listener);
    } else {
      this.globalFilterService.resetFilterSelection(key, false, listener);
    }
    this.refreshRows();
  }

  /**
   * Clears every active filter on the current catalog — the quick-filter
   * dropdowns and the DOA free-text search — restoring their default values.
   * Non-internal keys are also reset through the global filter service so other
   * widgets stay in sync; the internal `__search` key only filters locally.
   */
  clearFilters(): void {
    const vm = this.vmSubject.value;
    const activeKeys = Object.keys(vm.filters);
    if (!activeKeys.length) return;

    const listener = this.activeConfig?.filterListener || [];
    activeKeys.forEach((key) => {
      if (!key.startsWith('__')) {
        this.globalFilterService.resetFilterSelection(key, false, listener);
      }
    });

    this.patch({ filters: {} });
    this.refreshRows();
  }

  toggleRowSelection(rowId: string): void {
    const selectedRowId = this.vmSubject.value.selectedRowId === rowId ? null : rowId;
    this.patch({ selectedRowId });
  }

  startAddRow(): void {
    const config = this.activeConfig;
    if (!config) return;
    // Fetch only the dropdowns represented by this catalog's columns. Each
    // master card keeps its own value as a text input.
    const activeCatalogId = this.vmSubject.value.activeCatalogId;
    if (
      activeCatalogId !== 'business-segments' &&
      this.hasCatalogColumn(config, 'businessSegment', 'business_unit')
    ) {
      this.loadBusinessUnitOptions(CATALOG_DROPDOWN_PAYLOAD);
    }
    if (activeCatalogId !== 'region' && this.hasCatalogColumn(config, 'region')) {
      this.loadRegionOptions(CATALOG_DROPDOWN_PAYLOAD);
    }
    if (activeCatalogId !== 'country' && this.hasCatalogColumn(config, 'country')) {
      this.loadCountryOptions(CATALOG_DROPDOWN_PAYLOAD);
    }
    this.patch({
      isAdding: true,
      draftRow: {
        id: `draft-${Date.now()}`,
        active: true,
        ...config.newRowDefaults,
        ...this.currentAuditDates(),
      } as CatalogRow,
    });
  }

  /**
   * Loads Business Unit dropdown options using the supplied context payload.
   */
  private loadBusinessUnitOptions(payload?: Record<string, unknown>): void {
    const url = this.currentConfig?.businessUnitOptionsApi || BUSINESS_UNIT_OPTIONS_URL;
    this.subs.add(
      this.fetchDropdownOptions(url, [
        'business_unit',
        'businessUnit',
        'businessSegment',
        'business_segment',
        'name',
        'code',
      ], payload).subscribe({
        next: (options) => {
          this.businessUnitApiOptions = options;
          this.refreshRows();
        },
        error: () => this.showToast('Failed to load business units', 'error'),
      }),
    );
  }

  /**
   * Loads Region dropdown options using the supplied context payload.
   */
  private loadRegionOptions(payload?: Record<string, unknown>): void {
    const url = this.currentConfig?.regionOptionsApi || REGION_OPTIONS_URL;
    this.subs.add(
      this.fetchDropdownOptions(
        url,
        ['region', 'region_name', 'regionName', 'name', 'code'],
        payload,
      ).subscribe({
        next: (options) => {
          this.regionApiOptions = options;
          this.refreshRows();
        },
        error: () => this.showToast('Failed to load regions', 'error'),
      }),
    );
  }

  /**
   * Loads Country dropdown options using the supplied context payload.
   */
  private loadCountryOptions(payload?: Record<string, unknown>): void {
    const url = this.currentConfig?.countryOptionsApi || COUNTRY_OPTIONS_URL;
    this.subs.add(
      this.fetchDropdownOptions(
        url,
        ['country', 'country_name', 'countryName', 'name', 'code'],
        payload,
      ).subscribe({
        next: (options) => {
          this.countryApiOptions = options;
          this.refreshRows();
        },
        error: () => this.showToast('Failed to load countries', 'error'),
      }),
    );
  }

  private fetchDropdownOptions(
    url: string,
    fieldCandidates: string[],
    customPayload: Record<string, unknown> = { active_flag: 'Yes' },
  ) {
    const dataParam = {
      skipDefaultPayload: true,
      customPayload,
    };
    return this.configService
      .getConfig(url, dataParam, {}, 'post')
      .pipe(map((response: unknown) => this.mapOptions(response, fieldCandidates)));
  }

  private hasCatalogColumn(
    config: CatalogEntityConfig,
    key: string,
    apiKey?: string,
  ): boolean {
    return config.columns.some(
      (column) => column.key === key || (!!apiKey && column.apiKey === apiKey),
    );
  }

  private mapOptions(response: unknown, fieldCandidates: string[]): string[] {
    const items = this.extractOptionItems(response);
    const values = items
      .map((item) => {
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          for (const field of fieldCandidates) {
            const value = this.readNonEmptyScalar(record[field]);
            if (value) return value;
          }
          return null;
        }
        return this.readNonEmptyScalar(item);
      })
      .filter((value): value is string => !!value);

    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
  }

  private extractOptionItems(response: unknown): unknown[] {
    if (Array.isArray(response)) return response;
    if (!response || typeof response !== 'object') return [];

    const body = response as Record<string, unknown>;
    const candidates = ['content', 'data', 'rows', 'result', 'results', 'response', 'items', 'records'];
    for (const key of candidates) {
      const value = body[key];
      if (Array.isArray(value)) return value;
      if (value && typeof value === 'object') {
        const nested = this.extractOptionItems(value);
        if (nested.length) return nested;
      }
    }
    return [];
  }

  cancelAddRow(): void {
    this.patch({ isAdding: false, draftRow: null });
  }

  commitAddRow(): void {
    const vm = this.vmSubject.value;
    const draft = vm.draftRow;
    if (!draft || !vm.activeCatalogId) return;

    const codeKey = this.getPrimaryColumnKey();
    if (codeKey && !String(draft[codeKey] || '').trim()) {
      this.showToast(`${this.getPrimaryColumnLabel()} is required`, 'error');
      return;
    }

    const row: CatalogRow = {
      ...draft,
      ...this.currentAuditDates(),
      id: `${vm.activeCatalogId}-${Date.now()}`,
      active: this.resolveRowActive(draft),
      status: String(draft.status || 'Active'),
    };
    this.syncActiveFlagDisplay(row);

    // Append to the source order; the UI reverses on render, so the newly added
    // row still surfaces as the first row of the table.
    this.catalogRows[vm.activeCatalogId] = [...(this.catalogRows[vm.activeCatalogId] || []), row];

    // The tick only adds the row to the local UI: stage it as a pending insert
    // so it shows a Pending badge and is persisted only when the user clicks
    // Save. This holds for every catalog, API-backed or not.
    const pendingInsertIds = new Set(vm.pendingInsertIds);
    pendingInsertIds.add(row.id);

    this.patch({ isAdding: false, draftRow: null, pendingInsertIds });
    this.refreshRows();
  }

  /**
   * Stages a fully-specified row (from the DOA range form) without going
   * through the inline draft-row flow: appends it to the table and marks it as
   * a pending insert so the table's Save button persists it via `saveAll`.
   */
  addStagedRow(values: Partial<CatalogRow>): void {
    const vm = this.vmSubject.value;
    const config = this.activeConfig;
    if (!config || !vm.activeCatalogId) return;

    const row = {
      active: true,
      ...config.newRowDefaults,
      ...values,
      ...this.currentAuditDates(),
      id: `${vm.activeCatalogId}-${Date.now()}`,
    } as CatalogRow;
    row.status = String(row.status || 'Active');

    this.catalogRows[vm.activeCatalogId] = [...(this.catalogRows[vm.activeCatalogId] || []), row];

    const pendingInsertIds = new Set(vm.pendingInsertIds);
    if (config.dataApi) {
      pendingInsertIds.add(row.id);
    }

    this.patch({ pendingInsertIds });
    this.refreshRows();
  }

  /**
   * Resolves the display range for the selected approver level before inserting
   * the approver row. The approver API expects that value in its `ranges`
   * field, even though the approver form only captures `rangeLevel`.
   */
  addDoaApproverRow(values: Partial<CatalogRow>): void {
    const rangeLevel = values['rangeLevel'];
    const rangeEntity = this.vmSubject.value.config?.entities.find((entity) => entity.id === 'doa-range-level');
    const rangeApi = rangeEntity?.dataApi;

    if (!rangeApi?.url || rangeLevel === undefined || rangeLevel === null || rangeLevel === '') {
      this.showToast('Failed to resolve ranges for the selected range level', 'error');
      return;
    }

    const payload = {
      range_level: String(rangeLevel),
      is_deleted: false,
    };
    const dataParam = {
      skipDefaultPayload: true,
      customPayload: payload,
    };

    this.subs.add(
      this.configService.getConfig(rangeApi.url, dataParam, {}, rangeApi.method || 'post').subscribe({
        next: (response: unknown) => {
          const ranges = this.findResponseValue(response, 'ranges');
          if (ranges === undefined || ranges === null || ranges === '') {
            this.showToast('No ranges found for the selected range level', 'error');
            return;
          }
          this.addStagedRow({ ...values, ranges: String(ranges) });
        },
        error: () => this.showToast('Failed to resolve ranges for the selected range level', 'error'),
      }),
    );
  }

  /**
   * Stages a DOA row edit (from its upper New/Edit form) as a pending update:
   * the merged values live in `editValues` and the row id is flagged in
   * `editingIds`, so the table shows the queued values (read-only, no inline
   * inputs) with an "Edited" badge and the top Save button persists it via
   * `saveAll`. Nothing is sent to the API until Save is clicked.
   */
  updateDoaRow(rowId: string, values: Partial<CatalogRow>): void {
    const vm = this.vmSubject.value;
    const rows = this.catalogRows[vm.activeCatalogId] || [];
    const current = rows.find((row) => row.id === rowId);
    if (!this.activeConfig || !current) return;

    // The merged values live in `editValues`; flagging the row in `editingIds`
    // makes the table read from them (showing the queued change) and, for a
    // saved row, tells Save to send an update. A row already pending insert is
    // deduped by the pending counter and stays an insert on Save.
    const editValues = new Map(vm.editValues);
    editValues.set(rowId, { ...current, ...values } as CatalogRow);
    const editingIds = new Set(vm.editingIds);
    editingIds.add(rowId);

    this.patch({ editingIds, editValues });
    this.refreshRows();
  }

  toggleEdit(row: CatalogRow): void {
    const vm = this.vmSubject.value;
    const editingIds = new Set(vm.editingIds);
    const editValues = new Map(vm.editValues);

    if (editingIds.has(row.id)) {
      editingIds.delete(row.id);
      editValues.delete(row.id);
    } else if (row.active) {
      editingIds.add(row.id);
      editValues.set(row.id, { ...row });
      // Reuse the Add Row dropdown options for inline edit: fetch the Business
      // Unit and Region options (each shown for every catalog except its own)
      // so the editable fields render as dropdowns with the current value
      // pre-selected.
      const activeCatalogId = this.vmSubject.value.activeCatalogId;
      const config = this.activeConfig;
      if (
        config &&
        activeCatalogId !== 'business-segments' &&
        this.hasCatalogColumn(config, 'businessSegment', 'business_unit')
      ) {
        this.loadBusinessUnitOptions(CATALOG_DROPDOWN_PAYLOAD);
      }
      if (config && activeCatalogId !== 'region' && this.hasCatalogColumn(config, 'region')) {
        this.loadRegionOptions(CATALOG_DROPDOWN_PAYLOAD);
      }
      if (config && activeCatalogId !== 'country' && this.hasCatalogColumn(config, 'country')) {
        this.loadCountryOptions(CATALOG_DROPDOWN_PAYLOAD);
      }
    }

    this.patch({ editingIds, editValues });
    this.updatePendingCounts();
  }

  toggleActive(row: CatalogRow): void {
    const vm = this.vmSubject.value;

    if (vm.editingIds.has(row.id)) {
      const editValues = new Map(vm.editValues);
      const editRow = editValues.get(row.id);
      if (editRow) {
        const active = !editRow.active;
        editValues.set(row.id, {
          ...editRow,
          active,
          active_flag: this.toActiveFlagValue(active),
          status: active ? 'Active' : 'Archived',
        });
        this.patch({ editValues });
      }
      return;
    }

    this.catalogRows[vm.activeCatalogId] = (this.catalogRows[vm.activeCatalogId] || []).map((item) =>
      item.id === row.id
        ? {
            ...item,
            active: !item.active,
            active_flag: this.toActiveFlagValue(!item.active),
            status: !item.active ? 'Active' : 'Archived',
          }
        : item,
    );

    // Stage the toggle so "Save All" sends an update; toggling back before
    // saving un-stages it. Rows pending insert are already staged.
    if (!vm.pendingInsertIds.has(row.id)) {
      const pendingToggleIds = new Set(vm.pendingToggleIds);
      if (pendingToggleIds.has(row.id)) {
        pendingToggleIds.delete(row.id);
      } else {
        pendingToggleIds.add(row.id);
      }
      this.patch({ pendingToggleIds });
    }

    this.refreshRows();
  }

  /** Opens the "Delete Record?" confirmation popup for the given row. Nothing is deleted yet. */
  deleteRow(rowId: string): void {
    this.patch({ showDeleteConfirmModal: true, deleteConfirmRowId: rowId });
  }

  /** Closes the delete confirmation popup without making any changes. */
  cancelDeleteConfirm(): void {
    this.patch({ showDeleteConfirmModal: false, deleteConfirmRowId: null });
  }

  /**
   * Executes the delete for the row awaiting confirmation. Deletion is no
   * longer staged for "Save" — clicking Delete here immediately sends the
   * `is_deleted: true` request (mirroring the payload `saveAll` used to send)
   * and refreshes the table on success, so Save is only needed for create/edit.
   */
  confirmDeleteRow(): void {
    const vm = this.vmSubject.value;
    const rowId = vm.deleteConfirmRowId;
    if (!rowId) {
      this.cancelDeleteConfirm();
      return;
    }

    const editingIds = new Set(vm.editingIds);
    const editValues = new Map(vm.editValues);
    const pendingToggleIds = new Set(vm.pendingToggleIds);
    const pendingInsertIds = new Set(vm.pendingInsertIds);

    // Any pending edit/toggle on this row is moot once it is being deleted.
    editingIds.delete(rowId);
    editValues.delete(rowId);
    pendingToggleIds.delete(rowId);

    const entity = this.activeConfig;
    const activeCatalogId = vm.activeCatalogId;
    const isServerRow = !!entity?.dataApi && !pendingInsertIds.has(rowId);

    this.patch({
      editingIds,
      editValues,
      pendingToggleIds,
      showDeleteConfirmModal: false,
      deleteConfirmRowId: null,
    });

    if (isServerRow && entity) {
      const row = (this.catalogRows[activeCatalogId] || []).find((item) => item.id === rowId);
      if (!row) return;

      this.subs.add(
        this.saveRowToApi(row, entity, 'delete').subscribe({
          next: () => {
            this.catalogRows[activeCatalogId] = (this.catalogRows[activeCatalogId] || []).filter(
              (item) => item.id !== rowId,
            );
            this.refreshRows();
            this.showToast('Row deleted successfully');
          },
          error: (err) => this.handleSaveError(err),
        }),
      );
      return;
    }

    // Locally-added (never saved) rows and non-API catalogs are removed outright.
    this.catalogRows[activeCatalogId] = (this.catalogRows[activeCatalogId] || []).filter((row) => row.id !== rowId);
    pendingInsertIds.delete(rowId);
    this.patch({ pendingInsertIds });
    this.refreshRows();
    this.showToast('Row deleted');
  }

  saveAll(): void {
    const vm = this.vmSubject.value;
    if (!vm.editingIds.size && !vm.pendingInsertIds.size && !vm.pendingToggleIds.size && !vm.pendingDeleteIds.size) {
      this.showToast('No changes to save', 'error');
      return;
    }

    const updatedRows = (this.catalogRows[vm.activeCatalogId] || []).map((row) => {
      const edit = vm.editValues.get(row.id);
      const merged = edit ? { ...row, ...edit } as CatalogRow : { ...row };
      merged.active = this.resolveRowActive(merged);
      this.syncActiveFlagDisplay(merged);
      return merged;
    });

    // Give each pending row exactly one operation. This keeps the requests,
    // Save badge, and success message aligned even if staging sets overlap.
    const deleteIds = Array.from(vm.pendingDeleteIds);
    const insertIds = Array.from(vm.pendingInsertIds).filter(
      (id) => !vm.pendingDeleteIds.has(id),
    );
    const editIds = Array.from(vm.editingIds).filter(
      (id) => !vm.pendingInsertIds.has(id) && !vm.pendingDeleteIds.has(id),
    );
    const toggleOnlyIds = Array.from(vm.pendingToggleIds).filter(
      (id) =>
        !vm.editingIds.has(id) &&
        !vm.pendingInsertIds.has(id) &&
        !vm.pendingDeleteIds.has(id),
    );
    const savedCount = insertIds.length + editIds.length + toggleOnlyIds.length + deleteIds.length;
    const entity = this.activeConfig;

    const clearEditState = () => ({
      editingIds: new Set<string>(),
      editValues: new Map<string, CatalogRow>(),
      pendingInsertIds: new Set<string>(),
  pendingToggleIds: new Set<string>(),
  pendingDeleteIds: new Set<string>(),
    });

    if (entity?.dataApi) {
      const requests = [
        ...insertIds.map((id) => {
          const row = updatedRows.find((item) => item.id === id);
          return row ? this.saveRowToApi(row, entity, 'insert') : of(null);
        }),
        ...editIds.map((id) => {
          const row = updatedRows.find((item) => item.id === id);
          return row ? this.saveRowToApi(row, entity, 'update') : of(null);
        }),
        ...toggleOnlyIds.map((id) => {
          const row = updatedRows.find((item) => item.id === id);
          return row ? this.saveRowToApi(row, entity, 'update') : of(null);
        }),
        ...deleteIds.map((id) => {
          const row = updatedRows.find((item) => item.id === id);
          return row ? this.saveRowToApi(row, entity, 'delete') : of(null);
        }),
      ];
      // Editing a DOA range value must also propagate to the approver-level
      // records that reference it (the same fan-out the immediate save did).
      if (entity.id === 'doa-range-level') {
        editIds.forEach((id) => {
          const row = updatedRows.find((item) => item.id === id);
          if (row) requests.push(this.saveDoaRangeToApproverApi(row, entity));
        });
      }
      this.subs.add(
        forkJoin(requests).subscribe({
          next: () => {
            // Drop the deleted rows once the server has accepted the delete.
            this.catalogRows[vm.activeCatalogId] = updatedRows.filter((row) => !vm.pendingDeleteIds.has(row.id));
            this.patch(clearEditState());
            this.refreshRows();
            this.showToast(`${savedCount} row${savedCount > 1 ? 's' : ''} saved successfully`);
          },
          error: (err) => this.handleSaveError(err),
        }),
      );
      return;
    }

    this.catalogRows[vm.activeCatalogId] = updatedRows.filter((row) => !vm.pendingDeleteIds.has(row.id));
    this.patch(clearEditState());
    this.refreshRows();
    this.showToast(`${savedCount} row${savedCount > 1 ? 's' : ''} saved successfully`);
  }

  refresh(): void {
    const vm = this.vmSubject.value;
    if (!vm.config) return;

    // Reloading rows from source invalidates any staging on this card (inserts
    // that were never saved, toggles/edits against the old rows), so clear it.
    delete this.pendingByCatalog[vm.activeCatalogId];
    this.patch(this.emptyPendingState());

    const entity = this.activeConfig;
    if (entity?.dataApi) {
      this.apiLoadedCatalogIds.delete(entity.id);
      this.loadEntityRowsFromApi(entity);
      return;
    }

    const dataUrl = this.dataUrlOverride || vm.config.apiUrl;
    if (dataUrl.startsWith('http')) {
      delete this.catalogRows[vm.activeCatalogId];
      this.patch({ loading: true });
      this.fetchEntityRows(vm.config, dataUrl, vm.activeCatalogId);
    } else {
      this.loadRows(vm.config, dataUrl);
    }
  }

  openAddCatalogModal(): void {
    const available = this.getAvailableCatalogTypes();
    this.patch({
      availableCatalogTypes: available,
      newCatalogType: available[0]?.id || '',
      showAddCatalogModal: true,
    });
  }

  closeAddCatalogModal(): void {
    this.patch({ showAddCatalogModal: false, newCatalogType: '' });
  }

  setNewCatalogType(newCatalogType: string): void {
    this.patch({ newCatalogType });
  }

  getCatalogDescription(catalogId: string): string {
    return CATALOG_TYPES.find((catalog) => catalog.id === catalogId)?.description || '';
  }

  addCatalog(): void {
    const vm = this.vmSubject.value;
    const selected = CATALOG_TYPES.find((catalog) => catalog.id === vm.newCatalogType);

    if (!selected) {
      this.showToast('Select a catalog type', 'error');
      return;
    }

    if (!vm.config) return;

    const entity = this.createDynamicEntityConfig(selected);
    const config: DomainCatalogConfig = {
      ...vm.config,
      entities: [...vm.config.entities, entity],
    };

    this.catalogRows[entity.id] = [];
    this.patch({
      config,
      activeCatalogId: entity.id,
      navItems: this.toNavItems(config),
      filters: {},
      selectedRowId: null,
      editingIds: new Set<string>(),
      editValues: new Map<string, CatalogRow>(),
      pendingInsertIds: new Set<string>(),
  pendingToggleIds: new Set<string>(),
  pendingDeleteIds: new Set<string>(),
      isAdding: false,
      draftRow: null,
      showAddCatalogModal: false,
      newCatalogType: '',
      availableCatalogTypes: this.getAvailableCatalogTypes(config),
    });
    this.refreshRows();
    this.showToast(`${selected.label} catalog added`);
  }

  removeAddedCatalog(catalogId: string): void {
    const vm = this.vmSubject.value;
    if (!vm.config) return;

    const entity = vm.config.entities.find((item) => item.id === catalogId);
    if (!entity?.dynamic) return;

    const config: DomainCatalogConfig = {
      ...vm.config,
      entities: vm.config.entities.filter((item) => item.id !== catalogId),
    };
    delete this.catalogRows[catalogId];

    const activeCatalogId = vm.activeCatalogId === catalogId
      ? config.initialCatalogId || config.entities[0]?.id || ''
      : vm.activeCatalogId;

    this.patch({
      config,
      activeCatalogId,
      navItems: this.toNavItems(config),
      availableCatalogTypes: this.getAvailableCatalogTypes(config),
      filters: {},
      selectedRowId: null,
      editingIds: new Set<string>(),
      editValues: new Map<string, CatalogRow>(),
      pendingInsertIds: new Set<string>(),
  pendingToggleIds: new Set<string>(),
  pendingDeleteIds: new Set<string>(),
      isAdding: false,
      draftRow: null,
    });
    this.refreshRows();
  }

  private loadRows(config: DomainCatalogConfig, dataUrl: string): void {
    let activeCatalogId = config.initialCatalogId || config.entities[0]?.id || '';
    const initialEntity = config.entities.find((entity) => entity.id === activeCatalogId);
    if (initialEntity?.tabs?.length) {
      activeCatalogId = initialEntity.tabs[0].id;
    }
    this.apiLoadedCatalogIds.clear();
    this.patch({
      config,
      activeCatalogId,
      navItems: this.toNavItems(config),
      availableCatalogTypes: this.getAvailableCatalogTypes(config),
    });

    const activeEntity = config.entities.find((entity) => entity.id === activeCatalogId);
    if (activeEntity?.dataApi) {
      this.loadEntityRowsFromApi(activeEntity);
      return;
    }
    this.fetchEntityRows(config, dataUrl, activeCatalogId);
  }

  private fetchEntityRows(config: DomainCatalogConfig, dataUrl: string, catalogId: string): void {
    const entity = config.entities.find((e) => e.id === catalogId);
    const entityName = entity?.entityName || catalogId;
    const isApiEndpoint = dataUrl.startsWith('http');

    const apiConfig = config.apiConfig || {};
    const basePayload = (apiConfig['customPayload'] as Record<string, unknown>) || {};
    const dataParam = {
      ...apiConfig,
      customPayload: { ...basePayload, entityName },
    };
    const method = String(apiConfig['method'] || 'post');

    const request$ = isApiEndpoint
      ? this.configService.getConfig(dataUrl, dataParam, {}, method)
      : this.http.get<DomainCatalogPayload>(dataUrl);

    this.subs.add(
      request$.subscribe({
        next: (response: unknown) => {
          this.isLoading = false;
          if (isApiEndpoint) {
            const rows = this.normaliseApiRows(response as CatalogRow[], catalogId);
            this.catalogRows[catalogId] = rows;
          } else {
            this.catalogRows = this.extractCatalogs(response as DomainCatalogPayload);
          }
          this.patch({ loading: false });
          this.refreshRows();
        },
        error: () => {
          this.isLoading = false;
          this.patch({ config, loading: false });
        },
      }),
    );
  }

  private loadEntityRowsFromApi(entity: CatalogEntityConfig): void {
    const dataApi = entity.dataApi;
    if (!dataApi?.url) return;
    // Only ever fire the API once per catalog. Marking it here (before the
    // request resolves) prevents repeated hovers from re-triggering it while
    // the call is in flight or after it has already failed.
    if (this.apiLoadedCatalogIds.has(entity.id)) return;
    this.apiLoadedCatalogIds.add(entity.id);

    const method = (dataApi.method || 'post').toLowerCase();
    const dataParam = {
      skipDefaultPayload: true,
      customPayload: (dataApi.payload as Record<string, unknown>) ?? {},
    };

    this.patch({ loading: true });
    this.subs.add(
      this.configService.getConfig(dataApi.url, dataParam, {}, method).subscribe({
        next: (response: unknown) => {
          this.catalogRows[entity.id] = this.mapApiRows(response, entity);
          this.patch({ loading: false });
          this.refreshRows();
        },
        error: () => {
          this.catalogRows[entity.id] = [];
          this.patch({ loading: false });
          this.refreshRows();
          this.showToast(`Failed to load ${entity.title}`, 'error');
        },
      }),
    );
  }

  private mapApiRows(response: unknown, entity: CatalogEntityConfig): CatalogRow[] {
    const records = this.extractApiRecords(response);
    const usedIds = new Set<string>();
    return records.map((record, index) => {
      const row: CatalogRow = {
        id: this.resolveRowId(record, entity, index, usedIds),
        active: this.resolveActive(record),
      };
      entity.columns.forEach((column) => {
        row[column.key] = this.resolveApiField(record, column.key, column.apiKey, entity.dataApi?.responseMap);
      });
      this.syncActiveFlagDisplay(row);
      if (!row['status']) {
        row['status'] = row.active ? 'Active' : 'Archived';
      }
      return row;
    });
  }

  private extractApiRecords(response: unknown): Array<Record<string, unknown>> {
    if (Array.isArray(response)) {
      return response.filter((item) => item && typeof item === 'object') as Array<Record<string, unknown>>;
    }
    if (!response || typeof response !== 'object') return [];

    const body = response as Record<string, unknown>;
    const candidates = ['content', 'data', 'rows', 'result', 'results', 'response', 'items', 'records'];
    for (const key of candidates) {
      const value = body[key];
      if (Array.isArray(value)) return this.extractApiRecords(value);
      if (value && typeof value === 'object') {
        const nested = this.extractApiRecords(value);
        if (nested.length) return nested;
      }
    }
    return [];
  }

  /** Finds a scalar response field through the API's varying wrapper shapes. */
  private findResponseValue(response: unknown, field: string): string | number | boolean | null | undefined {
    if (!response || typeof response !== 'object') return undefined;

    if (Array.isArray(response)) {
      for (const item of response) {
        const value = this.findResponseValue(item, field);
        if (value !== undefined) return value;
      }
      return undefined;
    }

    const record = response as Record<string, unknown>;
    const matchingKey = Object.keys(record).find((key) => key.toLowerCase() === field.toLowerCase());
    if (matchingKey) {
      const value = record[matchingKey];
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
        return value;
      }
    }

    for (const value of Object.values(record)) {
      const nestedValue = this.findResponseValue(value, field);
      if (nestedValue !== undefined) return nestedValue;
    }
    return undefined;
  }

  private resolveApiField(
    record: Record<string, unknown>,
    columnKey: string,
    apiKey?: string,
    responseMap?: Record<string, string>,
  ): CatalogCellValue {
    const mappedKey = responseMap?.[columnKey];
    const snakeKey = columnKey.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
    const candidates = [
      ...(mappedKey ? [mappedKey] : []),
      ...(apiKey ? [apiKey] : []),
      columnKey,
      snakeKey,
      ...(API_FIELD_ALIASES[columnKey] || []),
    ];

    const lowerCaseKeys = new Map(Object.keys(record).map((key) => [key.toLowerCase(), key]));
    for (const candidate of candidates) {
      const actualKey = candidate in record ? candidate : lowerCaseKeys.get(candidate.toLowerCase());
      if (actualKey === undefined) continue;
      const value = record[actualKey];
      if (value === undefined || value === null) continue;
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
      }
    }
    return '';
  }

  private resolveActive(record: Record<string, unknown>): boolean {
    const raw =
      record['active'] ?? record['active_flag'] ?? record['activeFlag'] ?? record['is_active'] ?? record['isActive'] ?? record['enabled'];
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'string') {
      const normalized = raw.trim().toLowerCase();
      return !['false', 'inactive', 'no', 'n', '0'].includes(normalized);
    }
    const status = String(record['status'] ?? '').toLowerCase();
    return status !== 'archived' && status !== 'inactive';
  }

  private resolveRowActive(row: Record<string, unknown>): boolean {
    return this.resolveActive(row);
  }

  private toActiveFlagValue(active: boolean): 'Yes' | 'No' {
    return active ? 'Yes' : 'No';
  }

  private syncActiveFlagDisplay(row: CatalogRow): void {
    if ('active_flag' in row) {
      row['active_flag'] = this.toActiveFlagValue(row.active !== false);
    }
  }

  private normaliseApiRows(data: CatalogRow[], catalogId: string): CatalogRow[] {
    if (!Array.isArray(data)) return [];
    const usedIds = new Set<string>();
    const entity = this.vmSubject.value.config?.entities.find((item) => item.id === catalogId);
    return data.map((item, index) => {
      const record = item as Record<string, unknown>;
      const row = {
        ...item,
        id: this.resolveRowId(record, entity, index, usedIds),
        active: this.resolveActive(record),
        status: String(item['status'] || (this.resolveActive(record) ? 'Active' : 'Archived')),
      } as CatalogRow;
      this.syncActiveFlagDisplay(row);
      return row;
    });
  }

  private resolveRowId(
    record: Record<string, unknown>,
    entity: CatalogEntityConfig | undefined,
    index: number,
    usedIds: Set<string>,
  ): string {
    const directId = this.readNonEmptyScalar(record['uuid']) || this.readNonEmptyScalar(record['id']);
    if (directId) {
      return this.ensureUniqueRowId(directId, usedIds);
    }

    const businessKeyId = (entity?.businessKey || [])
      .map((key) => this.readNonEmptyScalar(record[key]))
      .filter((value): value is string => !!value)
      .join('::');
    if (businessKeyId) {
      return this.ensureUniqueRowId(`${entity?.id || 'catalog'}::${businessKeyId}`, usedIds);
    }

    const columnBasedId = (entity?.columns || [])
      .map((column) => this.readNonEmptyScalar(record[column.apiKey || column.key]))
      .filter((value): value is string => !!value)
      .slice(0, 3)
      .join('::');
    if (columnBasedId) {
      return this.ensureUniqueRowId(`${entity?.id || 'catalog'}::${columnBasedId}`, usedIds);
    }

    return this.ensureUniqueRowId(`${entity?.id || 'catalog'}-api-${index}`, usedIds);
  }

  private ensureUniqueRowId(baseId: string, usedIds: Set<string>): string {
    let candidate = baseId;
    let suffix = 1;
    while (usedIds.has(candidate)) {
      suffix += 1;
      candidate = `${baseId}--${suffix}`;
    }
    usedIds.add(candidate);
    return candidate;
  }

  private readNonEmptyScalar(value: unknown): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return null;
  }

  /**
   * Discards every unsaved change on the active catalog: drops locally-added
   * rows, reverts staged active toggles, and clears staged edits/deletes. The
   * saved (database) state stays untouched, so nothing is sent to the API.
   */
  discardChanges(): void {
    const vm = this.vmSubject.value;
    const count = getPendingCatalogChangeCount(vm);
    if (!count) {
      this.showToast('No pending changes to discard', 'error');
      return;
    }

    const catalogId = vm.activeCatalogId;
    const rows = (this.catalogRows[catalogId] || [])
      // Locally-added rows only ever existed in the UI: drop them outright.
      .filter((row) => !vm.pendingInsertIds.has(row.id))
      // Un-apply the staged active toggles that mutated the saved rows in place.
      .map((row) => {
        if (!vm.pendingToggleIds.has(row.id)) return row;
        const active = !row.active;
        return { ...row, active, status: active ? 'Active' : 'Archived' };
      });
    this.catalogRows[catalogId] = rows;

    delete this.pendingByCatalog[catalogId];
    this.patch(this.emptyPendingState());
    this.refreshRows();
    this.showToast(`${count} pending change${count > 1 ? 's' : ''} discarded`);
  }

  /** True when any catalog — active or not — still holds unsaved changes. */
  hasPendingChanges(): boolean {
    if (this.hasActivePendingChanges()) return true;
    return Object.values(this.pendingByCatalog).some(
      (snapshot) => getPendingCatalogChangeCount(snapshot) > 0,
    );
  }

  /** True when refreshing the currently displayed catalog would discard work. */
  hasActivePendingChanges(): boolean {
    return getPendingCatalogChangeCount(this.vmSubject.value) > 0;
  }

  private emptyPendingState(): CatalogPendingSnapshot {
    return {
      editingIds: new Set<string>(),
      editValues: new Map<string, CatalogRow>(),
      pendingInsertIds: new Set<string>(),
      pendingToggleIds: new Set<string>(),
      pendingDeleteIds: new Set<string>(),
    };
  }

  private currentAuditDates(): Pick<CatalogRow, 'created' | 'updatedDate'> {
    const today = new Date().toISOString().slice(0, 10);

    return {
      created: today,
      updatedDate: today,
    };
  }

  /** Stores the active catalog's staging so it survives a switch to another card. */
  private snapshotPending(vm: DomainCatalogViewModel): void {
    if (!vm.activeCatalogId) return;
    if (getPendingCatalogChangeCount(vm) > 0) {
      this.pendingByCatalog[vm.activeCatalogId] = {
        editingIds: new Set(vm.editingIds),
        editValues: new Map(vm.editValues),
        pendingInsertIds: new Set(vm.pendingInsertIds),
        pendingToggleIds: new Set(vm.pendingToggleIds),
        pendingDeleteIds: new Set(vm.pendingDeleteIds),
      };
    } else {
      delete this.pendingByCatalog[vm.activeCatalogId];
    }
  }

  /**
   * Returns the staging sets for the catalog being opened and removes them from
   * the off-screen store: while a catalog is active its staging lives on the
   * view model, so a stale snapshot would double-count it.
   */
  private restorePending(catalogId: string): CatalogPendingSnapshot {
    const stored = this.pendingByCatalog[catalogId];
    delete this.pendingByCatalog[catalogId];
    return {
      editingIds: new Set(stored?.editingIds),
      editValues: new Map(stored?.editValues),
      pendingInsertIds: new Set(stored?.pendingInsertIds),
      pendingToggleIds: new Set(stored?.pendingToggleIds),
      pendingDeleteIds: new Set(stored?.pendingDeleteIds),
    };
  }

  /** Recomputes the per-catalog pending badge counts (off-screen cards + the active one). */
  private updatePendingCounts(): void {
    this.patch({ pendingCounts: this.computePendingCounts() });
  }

  private computePendingCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const [id, snapshot] of Object.entries(this.pendingByCatalog)) {
      const count = getPendingCatalogChangeCount(snapshot);
      if (count) counts[id] = count;
    }
    const vm = this.vmSubject.value;
    if (vm.activeCatalogId) {
      const live = getPendingCatalogChangeCount(vm);
      if (live) {
        counts[vm.activeCatalogId] = live;
      } else {
        delete counts[vm.activeCatalogId];
      }
    }
    return getPendingCatalogCardCounts(counts, vm.config?.entities || []);
  }

  private refreshRows(): void {
    const vm = this.vmSubject.value;
    // Reverse at the UI layer only: the source API data in `catalogRows` is left
    // untouched, but the table binds to this reversed copy so the last record
    // from the API response (the latest) renders as the first row for every card.
    // A row marked for deletion stays visible, flagged as pending, so the user
    // can see (and undo) the mark before pressing Save.
    const rows = [...(this.catalogRows[vm.activeCatalogId] || [])].reverse();
    const filteredRows = this.filterRows(rows, vm.filters);
    this.patch({ rows, filteredRows, pendingCounts: this.computePendingCounts() });
  }

  private filterRows(rows: CatalogRow[], filters: Record<string, string>): CatalogRow[] {
    const entries = Object.entries(filters).filter(([, value]) => value.trim());
    if (!entries.length) return rows;

    return rows.filter((row) =>
      entries.every(([key, value]) => {
        const needle = value.toLowerCase();
        // `__search` is the free-text box on the DOA approver filter row: it
        // matches against every cell instead of a single column.
        if (key === '__search') {
          return Object.values(row).some((cell) => String(cell ?? '').toLowerCase().includes(needle));
        }
        return String(row[key] ?? '').toLowerCase().includes(needle);
      }),
    );
  }

  private applyIncomingFilters(requestParams: Record<string, unknown>): void {
    const config = this.activeConfig;
    if (!config) return;

    const supportedKeys = new Set(config.filters.map((filter) => filter.key));
    const nextFilters: Record<string, string> = {};

    Object.entries(requestParams).forEach(([key, value]) => {
      if (!supportedKeys.has(key)) return;
      const firstValue = Array.isArray(value) ? value[0] : value;
      if (firstValue !== undefined && firstValue !== null) {
        nextFilters[key] = String(firstValue);
      }
    });

    this.patch({ filters: nextFilters });
    this.refreshRows();
  }

  private extractCatalogs(payload: DomainCatalogPayload): Record<string, CatalogRow[]> {
    const direct = payload.response?.[0]?.data?.catalogs;
    if (direct) return direct;

    const responseList = payload.responseList?.[0]?.data?.[0]?.data?.catalogs;
    return responseList || {};
  }

  private toNavItems(config: DomainCatalogConfig): CatalogNavItem[] {
    return config.entities
      .filter((entity) => entity.sidebarCard?.visible !== false)
      .map((entity) => ({
      id: entity.id,
      label: entity.navLabel,
      shortLabel: entity.shortLabel,
      description: entity.sidebarCard?.description || entity.navDescription,
      icon: entity.icon,
      removable: entity.dynamic,
      visible: entity.sidebarCard?.visible,
      badgeLabel: entity.sidebarCard?.badgeLabel,
      displayType: entity.sidebarCard?.displayType,
      metaChips: entity.sidebarCard?.metaChips,
      statusLabel: entity.sidebarCard?.statusLabel,
      statusClass: entity.sidebarCard?.statusClass,
    }));
  }

  private getAvailableCatalogTypes(config = this.vmSubject.value.config): CatalogTypeOption[] {
    const existing = new Set((config?.entities || []).map((entity) => entity.id));
    return CATALOG_TYPES.filter((catalog) => !existing.has(catalog.id));
  }

  private createDynamicEntityConfig(catalog: CatalogTypeOption): CatalogEntityConfig {
    return {
      id: catalog.id,
      title: catalog.label,
      navLabel: catalog.label,
      navDescription: catalog.description,
      icon: 'grid',
      addButtonLabel: 'Add Row',
      filterListener: ['domain-catalogs-table'],
      dynamic: true,
      filters: [
        { key: 'code', label: catalog.label, placeholder: catalog.label, icon: 'tag' },
        { key: 'description', label: 'Description', placeholder: 'Description', icon: 'list' },
        { key: 'createdBy', label: 'Created by', placeholder: 'Created by', icon: 'user' },
      ],
      columns: [
        { key: 'code', label: catalog.label, editable: true, width: '160px' },
        { key: 'description', label: 'Description', editable: true, width: '260px' },
        { key: 'status', label: 'Status', type: 'status', editable: false, width: '100px' },
        { key: 'created', label: 'Created', type: 'date', editable: false, width: '110px' },
        { key: 'createdBy', label: 'Created By', type: 'email', editable: false, width: '170px' },
        { key: 'updatedDate', label: 'Updated Date', type: 'date', editable: false, width: '120px' },
        { key: 'updatedBy', label: 'Updated By', type: 'email', editable: false, width: '170px' },
      ],
      newRowDefaults: {
        code: '',
        description: '',
        status: 'Active',
        created: '26 Jun 2026',
        createdBy: 'admin@simadvisory.com',
        updatedDate: '26 Jun 2026',
        updatedBy: 'admin@simadvisory.com',
        active: true,
      },
    };
  }

  private saveRowToApi(row: CatalogRow, entity: CatalogEntityConfig, operation: 'insert' | 'update' | 'delete') {
    const url = this.resolveSaveUrl(entity);
    if (!url) return of(null);
    const payload = this.buildSavePayload(row, entity, operation);
    return this.configService
      .getConfig(url, { skipDefaultPayload: true, customPayload: payload }, {}, 'post')
      .pipe(map((response) => this.assertSaveSucceeded(response)));
  }

  /** Propagates an edited range to existing DOA approver-level records. */
  private saveDoaRangeToApproverApi(row: CatalogRow, rangeEntity: CatalogEntityConfig) {
    const url = this.resolveSaveUrl(rangeEntity);
    if (!url) return of(null);

    const approverEntity = this.vmSubject.value.config?.entities.find((entity) => entity.id === 'doa-approver-level');
    const ranges = String(row['ranges'] ?? '').replace(/\s*\/\s*/g, '/');
    const payload = {
      requestData: {
        ranges,
        range_level: String(row['rangeLevel'] ?? ''),
        businessKey: ['range_level'],
        operation: 'update',
      },
      responseList: [],
      entity: approverEntity ? this.resolveEntityName(approverEntity) : 'dh_doa_approver_level',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };

    return this.configService
      .getConfig(url, { skipDefaultPayload: true, customPayload: payload }, {}, 'post')
      .pipe(map((response) => this.assertSaveSucceeded(response)));
  }

  /**
   * Turns a save failure into a user-facing error toast. Duplicate/unique-constraint
   * violations get a contextual message naming the offending field and value;
   * every other failure gets a generic message. Raw SQL, constraint names, and
   * stack traces from the backend are never surfaced to the user.
   */
  private handleSaveError(err: unknown): void {
    const reason = this.extractSaveErrorReason(err);
    const duplicate = parseDuplicateSaveFailure(reason, this.activeConfig?.columns);
    const message = duplicate
      ? `The ${duplicate.fieldName} "${duplicate.value}" already exists. Please enter a unique ${duplicate.fieldName} and try again.`
      : 'We couldn\'t save your changes. Please check your input and try again.';
    this.showToast(message, 'error');
  }

  /**
   * Pulls the failure reason out of a save error. Application-level failures
   * arrive as an `Error` thrown by `assertSaveSucceeded` (already carrying the
   * reason); transport failures arrive as an `HttpErrorResponse` whose reason
   * lives in its `error` body. Returns an empty string when nothing useful is found.
   */
  private extractSaveErrorReason(err: unknown): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const httpBody = (err as { error: unknown }).error;
      const reason = getCatalogSaveFailureMessage(httpBody) || (typeof httpBody === 'string' ? httpBody.trim() : '');
      if (reason) return reason;
    }

    if (err instanceof Error && err.message && err.message !== 'Catalog API reported a save failure') {
      return err.message;
    }

    return '';
  }

  private assertSaveSucceeded(response: unknown): unknown {
    // These APIs return HTTP 200 even on failure; the top-level `error` field is
    // the source of truth (null = committed, any value = rolled back).
    const error = getSaveResponseError(response);
    if (error) {
      throw new Error(error);
    }
    return response;
  }

  /** Save posts to the platform base (e.g. /spriced/platform); the entity is carried in the payload. */
  private resolveSaveUrl(entity: CatalogEntityConfig): string {
    const url = entity.dataApi?.url || '';
    const match = url.match(/^(.*?\/spriced\/platform)/);
    return match?.[1] || url;
  }

  private buildSavePayload(row: CatalogRow, entity: CatalogEntityConfig, operation: 'insert' | 'update' | 'delete'): Record<string, unknown> {
    return {
      requestData: this.buildSaveRequestData(row, entity, operation),
      responseList: [],
      entity: this.resolveEntityName(entity),
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
  }

  private buildSaveRequestData(row: CatalogRow, entity: CatalogEntityConfig, operation: 'insert' | 'update' | 'delete'): Record<string, unknown> {
    const isDelete = operation === 'delete';
    const isDoaCatalog = entity.id === 'doa-range-level' || entity.id === 'doa-approver-level';
    const excludedTypes = new Set(['status', 'date', 'email']);
    const requestData: Record<string, unknown> = {};
    entity.columns.forEach((column) => {
      if (column.type && excludedTypes.has(column.type)) return;
      const requestKey = column.apiKey || column.key;
      const value = row[column.key] ?? '';
      requestData[requestKey] = isDoaCatalog && requestKey === 'range_level'
        ? String(value)
        : value;
    });
    if (!isDoaCatalog && entity.saveOptions?.includeActiveFlag !== false) {
      requestData['active_flag'] = this.toActiveFlagValue(this.resolveRowActive(row));
    }
    requestData['operation'] = operation;
    if (isDelete) {
      // Deletes are soft: the read APIs filter on `is_deleted: false`, so the
      // record only disappears once this flag is flipped.
      requestData['is_deleted'] = true;
    }
    if ((operation === 'update' || isDelete) && entity.businessKey) {
      requestData['businessKey'] = entity.businessKey;
    }
    return requestData;
  }

  private resolveEntityName(entity: CatalogEntityConfig): string {
    const url = entity.dataApi?.url || '';
    const match = url.match(/\/(dh_[a-zA-Z0-9_]+)/);
    return match?.[1] || entity.entityName || entity.id;
  }

  private getPrimaryColumnKey(): string {
    return this.activeConfig?.columns[0]?.key || '';
  }

  private getPrimaryColumnLabel(): string {
    return this.activeConfig?.columns[0]?.label || 'Primary value';
  }

  private showToast(toastMessage: string, toastVariant: 'success' | 'error' = 'success'): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.patch({ toastMessage, toastVariant, toastVisible: true });
    // Errors carry a full sentence to read/act on, so keep them up a bit longer.
    const duration = toastVariant === 'error' ? 6000 : 3000;
    this.toastTimer = setTimeout(() => this.patch({ toastVisible: false }), duration);
  }

  private patch(partial: Partial<DomainCatalogViewModel>): void {
    this.vmSubject.next({ ...this.vmSubject.value, ...partial });
  }
}
